import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Toast } from './Toast';
import { useRetailorJob } from '../context/RetailorJobContext';

function fmtElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Persistent cross-route notifier for the long-running retailor.
// While the user is on /app the in-page ProgressModal shows status, so the
// toast suppresses itself there to avoid double-notifying. The moment the
// user navigates elsewhere, the toast appears with elapsed time. When the
// retailor completes, a "View" action takes them back to the wizard.
export function RetailorBackgroundToast() {
  const job = useRetailorJob();
  const location = useLocation();
  const navigate = useNavigate();
  const [, force] = useState(0);

  useEffect(() => {
    if (job.status !== 'running') return;
    const id = window.setInterval(() => force(n => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [job.status]);

  // Suppress only when the in-page ProgressModal is doing the talking. The
  // user dismissing the modal flips inBackground to true, at which point we
  // surface the toast even on the wizard route.
  const onWizard = location.pathname === '/app';
  const modalOwnsProgress = onWizard && !job.inBackground;

  if (job.status === 'idle') return null;
  if (job.status === 'running' && modalOwnsProgress) return null;
  if (job.status === 'done' && modalOwnsProgress) return null;

  if (job.status === 'running') {
    const elapsed = job.startedAt ? Date.now() - job.startedAt : 0;
    return <Toast kind="info" message={`Tailoring your resume... (${fmtElapsed(elapsed)})`} />;
  }

  if (job.status === 'done') {
    return (
      <Toast
        kind="success"
        message="Your tailored resume is ready."
        onDismiss={job.acknowledge}
        action={{ label: 'View', onClick: () => { navigate('/app'); job.acknowledge(); } }}
      />
    );
  }

  return (
    <Toast
      kind="error"
      message={job.error || 'Tailoring failed. Please try again.'}
      onDismiss={job.acknowledge}
    />
  );
}
